import React from 'react';
import { Row, Col, Alert } from 'react-bootstrap';
import Button from '../Utils/Button';
import { restUrl } from '../../env';
import { WaitUntilLoggedIn } from '../Core/LoginManager';
import axios from 'axios';
import { gameStateManager } from '../../App';
import { Navigate } from "react-router-dom";
import '../../css/bugReportRow.css';

export default class RoomCleanup extends React.Component
{
  state = {
    cleanupStatus: null,
    isLoading: false,
    error: null,
    successMessage: null,
    isPerformingCleanup: false
  };

  async componentDidMount()
  {
    await WaitUntilLoggedIn( async () =>
    {
      await this.loadCleanupStatus();
    } );
  }

  async loadCleanupStatus()
  {
    try
    {
      this.setState( { isLoading: true, error: null } );

      const response = await axios.post( `${restUrl}/admin/roomCleanupStatus`, {}, { withCredentials: true } );

      if( response.data.success )
      {
        this.setState( {
          cleanupStatus: response.data.data,
          isLoading: false
        } );
      }
      else
      {
        this.setState( {
          error: response.data.error || 'Failed to load cleanup status',
          isLoading: false
        } );
        console.error( "Failed to get room cleanup status: ", response.data.error );
      }
    }
    catch( error )
    {
      this.setState( {
        error: 'Network error while loading cleanup status',
        isLoading: false
      } );
      console.error( "Error loading room cleanup status:", error );
    }
  }

  async performCleanup()
  {
    try
    {
      this.setState( {
        isPerformingCleanup: true,
        error: null,
        successMessage: null
      } );

      const response = await axios.post( `${restUrl}/admin/performRoomCleanup`, {}, { withCredentials: true } );

      if( response.data.success )
      {
        this.setState( {
          successMessage: `Cleanup completed! ${response.data.data.deletedCount} rooms were deleted.`,
          isPerformingCleanup: false
        } );

        // Refresh the status after cleanup
        await this.loadCleanupStatus();
      }
      else
      {
        this.setState( {
          error: response.data.error || 'Failed to perform cleanup',
          isPerformingCleanup: false
        } );
        console.error( "Failed to perform room cleanup: ", response.data.error );
      }
    }
    catch( error )
    {
      this.setState( {
        error: 'Network error while performing cleanup',
        isPerformingCleanup: false
      } );
      console.error( "Error performing room cleanup:", error );
    }
  }

  formatDateTime( dateTime )
  {
    if( !dateTime ) return 'N/A';
    return new Date( dateTime ).toLocaleString();
  }

  render()
  {
    const { cleanupStatus, isLoading, error, successMessage, isPerformingCleanup } = this.state;

    return (
      <div>
        <div className="room-cleanup-header" style={ { marginBottom: '20px' } }>
          <h4>Room Cleanup Management</h4>
          <div style={ { display: 'flex', gap: '10px' } }>
            <Button
              className="refresh-button"
              onClick={ () => this.loadCleanupStatus() }
              disabled={ isLoading }>
              { isLoading ? 'Loading...' : 'Refresh Status' }
            </Button>
            <Button
              className="cleanup-button"
              onClick={ () => this.performCleanup() }
              disabled={ isPerformingCleanup || !cleanupStatus }
              style={ {
                backgroundColor: '#dc3545',
                borderColor: '#dc3545',
                color: 'white'
              } }>
              { isPerformingCleanup ? 'Performing Cleanup...' : 'Perform Cleanup' }
            </Button>
          </div>
        </div>

        { error && (
          <Alert variant="danger" style={ { marginBottom: '20px' } }>
            { error }
          </Alert>
        ) }

        { successMessage && (
          <Alert variant="success" style={ { marginBottom: '20px' } }>
            { successMessage }
          </Alert>
        ) }

        <div className="room-cleanup-container">
          { cleanupStatus ? (
            <div className="room-cleanup-status">
              <Row className="mb-3">
                <Col>
                  <div className="panel" style={ { padding: '20px' } }>
                    <h5>Current Room Statistics</h5>
                    <Row>
                      <Col md={ 3 }>
                        <div className="stat-item">
                          <div className="stat-label">Total Rooms</div>
                          <div className="stat-value" style={ { fontSize: '24px', fontWeight: 'bold', color: '#007bff' } }>
                            { cleanupStatus.totalRooms }
                          </div>
                        </div>
                      </Col>
                      <Col md={ 3 }>
                        <div className="stat-item">
                          <div className="stat-label">Started Rooms</div>
                          <div className="stat-value" style={ { fontSize: '24px', fontWeight: 'bold', color: '#28a745' } }>
                            { cleanupStatus.startedRooms }
                          </div>
                        </div>
                      </Col>
                      <Col md={ 3 }>
                        <div className="stat-item">
                          <div className="stat-label">Non-Started Rooms</div>
                          <div className="stat-value" style={ { fontSize: '24px', fontWeight: 'bold', color: '#6c757d' } }>
                            { cleanupStatus.nonStartedRooms }
                          </div>
                        </div>
                      </Col>
                      <Col md={ 3 }>
                        <div className="stat-item">
                          <div className="stat-label">Old Non-Started Rooms</div>
                          <div className="stat-value" style={ { fontSize: '24px', fontWeight: 'bold', color: '#dc3545' } }>
                            { cleanupStatus.oldNonStartedRooms }
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col>
                  <div className="panel" style={ { padding: '20px' } }>
                    <h5>Cleanup Information</h5>
                    <p>
                      <strong>Cutoff Time:</strong> { this.formatDateTime( cleanupStatus.cutoffTime ) }
                    </p>
                    <p style={ { marginBottom: '10px' } }>
                      Rooms that are not started and were created before the cutoff time will be deleted during cleanup.
                    </p>

                    { cleanupStatus.oldNonStartedRooms > 0 ? (
                      <Alert variant="warning">
                        <strong>{ cleanupStatus.oldNonStartedRooms } old non-started room(s)</strong> are eligible for cleanup.
                        These rooms will be permanently deleted if you perform a cleanup.
                      </Alert>
                    ) : (
                      <Alert variant="info">
                        No old non-started rooms found. All rooms are either recently created or have been started.
                      </Alert>
                    ) }
                  </div>
                </Col>
              </Row>
            </div>
          ) : (
            !isLoading && !error && (
              <div style={ { textAlign: 'center', padding: '40px', color: '#6c757d' } }>
                No cleanup status available. Click "Refresh Status" to load the current room statistics.
              </div>
            )
          ) }

          { isLoading && (
            <div style={ { textAlign: 'center', padding: '40px', color: '#6c757d' } }>
              Loading room cleanup status...
            </div>
          ) }
        </div>
      </div>
    );
  }
}
